import React, { useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';

import { debounce } from '@mui/material';

import Box from '@mui/material/Box';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoRounded from '@mui/icons-material/InfoRounded';

import Destination from './Destination';
import { CreateCustomerAuthContext, initialStateStep4 } from '../Create';

const styles = {
  infoIcon: {
    color: 'graphic.main',
  },
  stack: {
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
};

export const ITN_REGEX = /^[a-z0-9]+$/i;
export const ITN_AUTH_TYPES = ['ONELOAD', 'ONETIME'];
export const ITN_LENGTH = 15;
const ITN_LENGTH_ERROR = 'ITN must be 15 characters.';
const ITN_ALPHANUMERIC_ERROR = 'ITN must only contain alphanumeric characters.';
const ITN_TOOLTIP =
  'Use of the Internal Transaction Number ("ITN") field is recommended for international barrel transactions.';

const LoadingControlDetails = ({ controlNumber }) => {
  const {
    destinationAddress: address,
    destinationCity: city,
    destinationState: state,
    destinationZipCode: zipCode,
    destinationIdName,
    destinationName,
    idName,
  } = controlNumber;

  const defaultToEmpty = R.defaultTo('');

  const createAddress = () =>
    `${defaultToEmpty(address)}${address ? ',' : ''}${defaultToEmpty(city)}${
      city ? ',' : ''
    }${defaultToEmpty(state)} ${defaultToEmpty(zipCode)}`;

  const { info, setInfo } = useContext(CreateCustomerAuthContext);
  const { authorizationType, controlNumbers } = info;
  const index = R.findIndex(R.propEq('idName', idName), controlNumbers);
  const itnValue = controlNumber.itn || '';

  const showItn = ITN_AUTH_TYPES.some(type => type === authorizationType);

  const [checked, setChecked] = useState(false);
  const [isExpanded, setExpanded] = useState(false);
  const [itnOpen, setItnOpen] = useState(!!controlNumber.itn);
  const [itnErrorProps, setItnErrorProps] = useState();

  useEffect(() => {
    const savedToggleState = localStorage.getItem(`isCustomerAuthorized-${idName}`);
    if (savedToggleState !== null) {
      setChecked(JSON.parse(savedToggleState));
    }
  }, [idName]);

  useEffect(() => {
    // Save the toggle state to local storage whenever it changes
    localStorage.setItem(`isCustomerAuthorized-${idName}`, JSON.stringify(checked));
  }, [checked, idName]);

  const handleChangeLD = event => {
    if (checked) {
      R.compose(
        setInfo,
        R.curryN(2, R.compose(R.mergeLeft(initialStateStep4), R.assoc('controlNumbers'))),
        R.always(
          R.update(
            R.findIndex(R.propEq('idName', idName), controlNumbers),
            R.pick(['idName'], R.find(R.propEq('idName', idName), controlNumbers)),
            controlNumbers
          )
        )
      )();
    }
    setChecked(event.target.checked);
  };

  const validateItn = ({ target: { value } }) =>
    setItnErrorProps(
      value.length === ITN_LENGTH
        ? value.match(ITN_REGEX)
          ? null
          : { error: true, helperText: ITN_ALPHANUMERIC_ERROR }
        : { error: true, helperText: ITN_LENGTH_ERROR }
    );

  const itnMemo = useMemo(() => debounce(validateItn, 2000), []);

  const updateItn = itn => {
    setInfo({
      ...info,
      controlNumbers: R.update(index, { ...controlNumbers[index], itn }, controlNumbers),
    });
  };

  const handleChangeItn = event => {
    setItnErrorProps();
    itnMemo(event);
    updateItn(event.target.value);
  };

  const handleToggleItn = ({ target }) => {
    setItnErrorProps();
    setItnOpen(target.checked);
    updateItn(target.checked ? '' : undefined);
  };

  return (
    <Accordion
      expanded={isExpanded}
      onChange={() => setExpanded(!isExpanded)}
      sx={{
        '&.Mui-expanded': {
          '&:before': { opacity: 1 },
          '&:first-of-type': {
            marginBottom: 2,
          },
          '&:last-of-type': {
            marginTop: 2,
          },
          margin: theme => theme.spacing(1, 0),
        },
        '&:first-of-type': {
          '&:last-of-type': {
            margin: 0,
          },
          margin: 0,
        },
        '&:last-of-type': {
          marginTop: 2,
        },
        boxShadow: 'none',
        margin: theme => theme.spacing(1, 0),
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          '.MuiAccordionSummary-content': {
            m: 0,
            minHeight: 56,
          },
          '.MuiAccordionSummary-expandIconWrapper': {
            // eslint-disable-next-line sort-keys-fix/sort-keys-fix
            alignSelf: { xs: 'start', sm: 'center' },
            p: 1,
          },
        }}
      >
        <Box
          sx={{
            alignItems: 'center',
            columnGap: 1,
            display: 'grid',
            // eslint-disable-next-line sort-keys-fix/sort-keys-fix
            gridTemplateColumns: { xs: '0.25fr 0.25fr 1fr', sm: '0.5fr 0.5fr 3fr' },
            gridTemplateRows: 'repeat(1fr)',
            pt: 0,
            rowGap: 0.5,
            width: '100%',
          }}
        >
          <Typography sx={{ alignSelf: 'center', gridColumn: '1' }} variant="subtitle2">
            {idName}
          </Typography>

          <>
            {destinationIdName && (
              <Typography sx={{ gridColumn: '2' }} variant="body2">
                {destinationIdName}
              </Typography>
            )}
            {destinationName && (
              <Box
                sx={{
                  alignSelf: address ? 'end' : 'center',
                  columnGap: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  // eslint-disable-next-line sort-keys-fix/sort-keys-fix
                  gridColumn: { xs: '1 / 4', sm: '3' },
                }}
              >
                <Typography variant="body2">{destinationName}</Typography>
                <Typography variant="body2">{createAddress()}</Typography>
              </Box>
            )}
          </>
        </Box>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          backgroundColor: 'surface.white',
          py: 0,
        }}
      >
        {showItn && (
          <>
            <Stack direction="row" spacing={2} sx={styles.stack}>
              <FormControlLabel
                control={<Switch checked={itnOpen} onChange={handleToggleItn} />}
                label="ITN"
                sx={{ mr: 0 }}
              />

              <Tooltip enterTouchDelay={10} title={ITN_TOOLTIP}>
                <InfoRounded sx={styles.infoIcon} />
              </Tooltip>
            </Stack>
            {itnOpen && (
              <TextField
                helperText=" "
                onBlur={validateItn}
                onChange={handleChangeItn}
                onFocus={() => setItnErrorProps()}
                sx={{ width: '100%' }}
                value={itnValue}
                variant="outlined"
                {...itnErrorProps}
              />
            )}
          </>
        )}
        <Stack direction="row" spacing={2} sx={styles.stack}>
          <FormControlLabel
            control={<Switch checked={checked} onChange={handleChangeLD} />}
            label="Link Destination"
          />
        </Stack>
        {checked && !destinationIdName && <Destination idName={idName} />}
      </AccordionDetails>
    </Accordion>
  );
};

LoadingControlDetails.propTypes = {
  controlNumber: PropTypes.shape({
    destinationAddress: PropTypes.string,
    destinationCity: PropTypes.string,
    destinationIdName: PropTypes.string,
    destinationName: PropTypes.string,
    destinationState: PropTypes.string,
    destinationZipCode: PropTypes.string,
    idName: PropTypes.string,
    itn: PropTypes.string,
  }).isRequired,
};

export default LoadingControlDetails;
